/**
 * HotspotContext — 全局热点数据 Context
 *
 * 参照 ContentOS 2 的 useContentStore (Zustand persist) 模式，
 * 将热点数据提升到 Context 中，避免 HotspotPage 组件卸载时数据丢失。
 *
 * 数据生命周期：
 *   Provider 挂载 → 从 localStorage 恢复缓存 → 保存到内存
 *   HotspotPage 挂载 → 直接读取 context 中的内存数据（秒加载）
 *   API fetch 成功 → 更新 context + 写入 localStorage
 *   HotspotPage 卸载 → context 数据仍然在内存中
 *   再次切换回来 → 秒加载，无需重新请求
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useMessage } from './MessageContext';
import { HOTSPOT_PLATFORMS, getCachedHotspots, refreshHotspots, HOTSPOT_CREDIT_COST } from '../services/hotspotService';
import { getHotspotsFromCache, saveHotspotsToCache, getCacheInfo } from '../utils/hotspotCache';

const HotspotContext = createContext(null);

export const useHotspot = () => useContext(HotspotContext);

/* ── Helper: build initial map from localStorage ── */
const buildInitialMap = () => {
    const map = {};
    HOTSPOT_PLATFORMS.forEach((p) => {
        const cached = getHotspotsFromCache(p.id);
        if (cached && cached.length > 0) {
            map[p.id] = cached;
        }
    });
    return map;
};

export const HotspotProvider = ({ children }) => {
    const { token, refreshProfile } = useAuth();
    const { showMessage } = useMessage();

    // ── Core data (survives HotspotPage unmount) ──
    const [hotspotsMap, setHotspotsMap] = useState(buildInitialMap);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // ── UI preferences (persisted to localStorage) ──
    const [selectedPlatforms, setSelectedPlatforms] = useState(() => {
        try {
            const saved = localStorage.getItem('contentos_hotspot_platforms');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) return new Set(parsed);
            }
        } catch (e) { /* ignore */ }
        return new Set(HOTSPOT_PLATFORMS.map((p) => p.id));
    });

    const [sortBy, setSortBy] = useState(() => {
        return localStorage.getItem('contentos_hotspot_sort') || 'heat';
    });

    // Persist preferences
    useEffect(() => {
        localStorage.setItem('contentos_hotspot_platforms', JSON.stringify([...selectedPlatforms]));
    }, [selectedPlatforms]);

    useEffect(() => {
        localStorage.setItem('contentos_hotspot_sort', sortBy);
    }, [sortBy]);

    // ── Ref for async access ──
    const hotspotsMapRef = useRef(hotspotsMap);
    useEffect(() => { hotspotsMapRef.current = hotspotsMap; }, [hotspotsMap]);

    // ── Load data for selected platforms (incremental) ──
    const loadPlatforms = useCallback(async (platformIds) => {
        if (!token) return;

        const toFetch = [];
        const currentMap = hotspotsMapRef.current;

        platformIds.forEach((pid) => {
            // Already in memory → skip
            if (currentMap[pid] && currentMap[pid].length > 0) return;

            // Try localStorage
            const cached = getHotspotsFromCache(pid);
            if (cached && cached.length > 0) {
                setHotspotsMap((prev) => {
                    if ((prev[pid] || []).length > 0) return prev;
                    return { ...prev, [pid]: cached };
                });
                return;
            }

            // Need to fetch from API
            toFetch.push(pid);
        });

        if (toFetch.length === 0) return;

        setLoading(true);
        try {
            const results = await Promise.allSettled(
                toFetch.map((pid) =>
                    getCachedHotspots(pid, token).then((r) => ({ pid, data: r.data || [] }))
                ),
            );

            setHotspotsMap((prev) => {
                const next = { ...prev };
                results.forEach((r) => {
                    if (r.status === 'fulfilled' && r.value.data.length > 0) {
                        next[r.value.pid] = r.value.data;
                        saveHotspotsToCache(r.value.pid, r.value.data);
                    }
                });
                return next;
            });
        } catch (error) {
            showMessage('error', error.message || '获取热点失败');
        } finally {
            setLoading(false);
        }
    }, [token, showMessage]);

    // ── Auto-load when selectedPlatforms change ──
    useEffect(() => {
        loadPlatforms([...selectedPlatforms]);
    }, [selectedPlatforms, loadPlatforms]);

    // ── Refresh (force re-fetch, costs credits) ──
    const refreshAll = useCallback(async () => {
        if (!token) {
            showMessage('warning', '请先登录后再刷新热点');
            return;
        }

        setRefreshing(true);
        try {
            // 调用全平台刷新端点（扣 10 积分）
            const res = await refreshHotspots('', token);
            const allTrends = res.data || [];

            // 按 source_name 分发到各平台
            const PLATFORM_SOURCE_MAP = {
                weibo: '微博', zhihu: '知乎', douyin: '抖音',
                baidu: '百度', bilibili: '哔哩哔哩',
            };

            setHotspotsMap((prev) => {
                const next = { ...prev };
                HOTSPOT_PLATFORMS.forEach((p) => {
                    const sourceName = PLATFORM_SOURCE_MAP[p.id] || p.id;
                    const items = allTrends.filter((t) =>
                        (t.source_name || '').includes(sourceName)
                    );
                    if (items.length > 0) {
                        next[p.id] = items;
                        saveHotspotsToCache(p.id, items);
                    }
                });
                return next;
            });

            showMessage('success', `刷新成功，获取到 ${allTrends.length} 条热点（消耗 ${HOTSPOT_CREDIT_COST} 积分）`);
            // 刷新用户积分余额
            refreshProfile?.();
        } catch (error) {
            showMessage('error', error.message || '刷新失败');
        } finally {
            setRefreshing(false);
        }
    }, [token, showMessage]);

    // ── Platform toggle helpers ──
    const togglePlatform = useCallback((pid) => {
        setSelectedPlatforms((prev) => {
            const next = new Set(prev);
            if (next.has(pid)) {
                if (next.size <= 1) return prev;
                next.delete(pid);
            } else {
                next.add(pid);
            }
            return next;
        });
    }, []);

    const toggleAll = useCallback(() => {
        const allIds = HOTSPOT_PLATFORMS.map((p) => p.id);
        setSelectedPlatforms((prev) => {
            if (prev.size === allIds.length) return prev;
            return new Set(allIds);
        });
    }, []);

    // ── Cache info for UI ──
    const cacheInfo = useMemo(() => {
        const first = [...selectedPlatforms][0];
        return first ? getCacheInfo(first) : null;
    }, [selectedPlatforms, hotspotsMap]); // eslint-disable-line react-hooks/exhaustive-deps

    const value = useMemo(() => ({
        hotspotsMap,
        loading,
        refreshing,
        selectedPlatforms,
        sortBy,
        cacheInfo,
        setSortBy,
        togglePlatform,
        toggleAll,
        refreshAll,
    }), [hotspotsMap, loading, refreshing, selectedPlatforms, sortBy, cacheInfo, setSortBy, togglePlatform, toggleAll, refreshAll]);

    return <HotspotContext.Provider value={value}>{children}</HotspotContext.Provider>;
};
